import { i18n } from '@localization'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@zet/common/components/toast'
import { ONE_HOUR } from '@zet/utils/cacheTime'

import { useNavigation } from '@hooks/useNavigation'

import { useSetAuthTokenState } from '../../api/signup/hooks/useVerifyOtp'
import {
  getCreditScoreUrl,
  getCustomerCount,
  getLeadCount,
  getUserAddCustomerQRCode,
  getUserSummary,
  signupViaTruecaller,
  updateAppOpenCount,
  updateEmailId,
  updateFreshchatRestoreTokenId,
  updateUserProfile,
} from './functions'

function useCustomerCount() {
  return useQuery(['userCustomerCount'], getCustomerCount)
}

function useLeadCount() {
  return useQuery(['userLeadCount'], getLeadCount)
}

function useUserSummary() {
  return useQuery(['userSummary'], getUserSummary)
}

function useCreditScoreUrl() {
  return useQuery(['creditScoreUrl'], getCreditScoreUrl, {
    staleTime: ONE_HOUR,
  })
}

function useUpdateUserProfileDetails() {
  const toast = useToast()
  return useMutation(updateUserProfile, {
    onSuccess(response) {
      if (response?.error) {
        toast?.show({
          intent: 'error',
          heading: i18n.t('toastMessages.error'),
          description: response?.error?.message,
          hideAfter: 3000,
        })
      }
    },
    onError(error) {
      toast?.show({
        intent: 'error',
        heading: i18n.t('toastMessages.error'),
        description: i18n.t('toastMessages.userDetailsNotUpdated'),
        hideAfter: 3000,
      })
    },
  })
}

function useUpdateFreshchatRestoreTokenId() {
  const queryClient = useQueryClient()
  return useMutation(updateFreshchatRestoreTokenId, {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
      queryClient.invalidateQueries({ queryKey: ['userContext'] })
    },
  })
}

function useUserEmailId() {
  const queryClient = useQueryClient()
  return useMutation(updateEmailId, {
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['home-widgets'] })
      queryClient.invalidateQueries({ queryKey: ['userContext'] })
    },
  })
}

function useUpdateAppOpenCount() {
  return useMutation(updateAppOpenCount)
}
function useUserAddCustomerQRCode() {
  return useQuery(['useUserAddCustomerQRCode'], getUserAddCustomerQRCode)
}

function useSignupViaTruecaller() {
  const { navigate } = useNavigation()
  const setAuthTokenState = useSetAuthTokenState()
  return useMutation(signupViaTruecaller, {
    onSuccess(data) {
      setAuthTokenState(data, true)
    },
    onError() {
      navigate('VerifyPhone')
    },
  })
}

export {
  useCreditScoreUrl,
  useCustomerCount,
  useLeadCount,
  useSignupViaTruecaller,
  useUpdateAppOpenCount,
  useUpdateFreshchatRestoreTokenId,
  useUpdateUserProfileDetails,
  useUserAddCustomerQRCode,
  useUserEmailId,
  useUserSummary,
}
